import React, { useState, useEffect } from 'react';
import { trackUserJourney, setUserContext } from '../utils/datadog';
import { getRumUser } from '../utils/users';

interface PaymentAuthorizeResponse {
  decision: string;
  transactionId: string;
  riskScore: number;
}

const MERCHANTS = [
  'Barkbucks Coffee',
  'The Dog House Diner',
  'Woof Mart',
  'Paws & Claws Pet Supply',
  'Fetch Electronics',
];

const PaymentPage: React.FC = () => {
  const username = getRumUser();

  const [formData, setFormData] = useState({
    merchant: MERCHANTS[0],
    amount: '129.99',
    cardNumber: '4242 4242 4242 4242',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<PaymentAuthorizeResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Bitsburg Bank - Make a Payment';
    setUserContext(username);
    trackUserJourney('payment_page_visited', { timestamp: new Date().toISOString() });
  }, [username]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setResult(null);
    trackUserJourney('payment_submitted', { merchant: formData.merchant, amount: formData.amount });

    try {
      // Same-origin relative call: RUM propagates the trace into the Bitsburg backend and on to
      // payment-auth -> txn-processor -> BitsburgDB.
      const response = await fetch('/api/payment/authorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardId: formData.cardNumber.replace(/\s+/g, ''),
          amount: parseFloat(formData.amount) || 0,
          merchant: formData.merchant,
          currency: 'USD',
        }),
      });

      if (!response.ok) {
        setError('Payment authorization is temporarily unavailable. Please try again.');
        trackUserJourney('payment_failed', { status: response.status });
        return;
      }

      const data: PaymentAuthorizeResponse = await response.json();
      setResult(data);
      trackUserJourney('payment_result', { decision: data.decision });
    } catch {
      setError('Payment authorization is temporarily unavailable. Please try again.');
      trackUserJourney('payment_failed', { status: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  const alertClass = (decision: string) => {
    if (decision === 'Settled' || decision === 'Approved') return 'bank-alert-success';
    if (decision === 'Declined') return 'bank-alert-warning';
    return 'bank-alert-danger';
  };

  return (
    <div className="py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="bank-card bank-card-elevated bank-card-no-hover">
              <div className="bank-card-header">
                <h3 className="mb-0 fw-bold">
                  <i className="bi bi-credit-card me-2"></i>
                  Make a Payment
                </h3>
                <p className="mb-0 mt-2 opacity-75">Authorize a payment to one of your merchants</p>
              </div>
              <div className="bank-card-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="bank-form-label" htmlFor="payment-merchant">
                      Merchant
                    </label>
                    <select
                      className="form-select bank-form-control"
                      id="payment-merchant"
                      name="merchant"
                      value={formData.merchant}
                      onChange={handleChange}
                    >
                      {MERCHANTS.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="bank-form-label" htmlFor="payment-amount">
                      Amount (USD)
                    </label>
                    <input
                      className="form-control bank-form-control"
                      id="payment-amount"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="bank-form-label" htmlFor="payment-card">
                      Card Number
                    </label>
                    <input
                      className="form-control bank-form-control"
                      id="payment-card"
                      name="cardNumber"
                      value={formData.cardNumber}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="text-center">
                    <button
                      className="bank-btn-primary px-4"
                      id="payment-submit-btn"
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Authorizing…' : 'Pay Now'}
                    </button>
                  </div>
                </form>

                {result && (
                  <div id="payment-result" className={`alert bank-alert mt-4 ${alertClass(result.decision)}`}>
                    <strong>{result.decision}</strong>
                    {result.transactionId && <div className="small">Transaction: {result.transactionId}</div>}
                  </div>
                )}
                {error && (
                  <div id="payment-error" className="alert bank-alert bank-alert-danger mt-4">
                    {error}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
